/**
 * Canonical tenant-branding → CSS-variable mapping (spec §8b; branding scope
 * locked 2026-07-14: tenant controls logo + primary + accent only).
 *
 * ONE source of truth so a tenant's `primary_color` always means --brand-primary
 * on every surface — fixing the pre-existing split where apps/help mapped
 * primary_color → --brand-accent while PrintableInvoice mapped it → --brand-primary.
 *
 * Reuses the color engine so the derived shades (hover / foreground / link) are
 * WCAG-AA guaranteed against the surface, not raw pass-throughs. Everything the
 * tenant does NOT control (neutrals, --semantic-* status/auction colors, fonts,
 * radii) is intentionally absent here — those stay AuctiSync-fixed.
 */

import { clamp, hexToRgb, hslToRgb, rgbToHex, rgbToHsl } from './convert.js';
import {
  AA_NORMAL,
  accessibleFill,
  bestTextOn,
  deepSurface,
  ensureContrast,
  mutedOn,
} from './contrast.js';
import { CTA_SURFACE_ELEV, ctaFromAccent } from './cta.js';
import type { RGB } from './types.js';

/** The tenant-branding blob stored in tenants.metadata.branding. Only
 * logo/primary/accent/deep are part of the color contract; the rest are
 * passthrough metadata other readers use. `secondary_color` is a legacy alias
 * for accent.
 *
 * ⚠️ THE COLOUR CONTRACT WIDENED ON 2026-09-11. It had been locked at
 * logo + primary + accent since 2026-07-14; `dark_color` is the third, added on
 * Jim's instruction. */
export interface TenantBranding {
  logo_url?: string;
  /** Legacy alias some rows use for the logo. */
  logo?: string;
  primary_color?: string;
  accent_color?: string;
  /** Legacy alias for accent_color. */
  secondary_color?: string;
  /**
   * The house's DARK colour — the near-black behind the catalogue's sale header
   * and filter rail.
   *
   * 🔴 THE THIRD BRAND COLOUR (2026-09-11). It was derived from the primary
   * until Jim asked for it to be a house's own choice: *"Let's add a third brand
   * color per house."* Discount Tool Auctions was the case that prompted it —
   * their site uses a near-black bar that nothing in our data captured, so we
   * inferred one from their mid-grey primary.
   *
   * OPTIONAL, and absent for every house that has not picked one. When it is
   * missing `deepSurface(primary)` still supplies one, so no tenant is left with
   * an unpainted panel and nobody has to answer a question they do not care
   * about. Stored wins over derived.
   */
  dark_color?: string;
  /**
   * 🔴 LEGACY ALIAS for `dark_color`, exactly like `secondary_color` is for
   * `accent_color`.
   *
   * Jim, 2026-09-17: *"I think deep_color is vague. It is not a branding
   * standard. Dark would be ok."* He is right — "deep" is our internal word for
   * the near-black surface (`--brand-deep`, `bidderTheme.brandDeep`) and it had
   * leaked into the one name a tenant actually stores.
   *
   * NO ROW WAS REWRITTEN. Readers take `dark_color ?? deep_color`, so a house
   * that stored the old key keeps its colour and moves to the new name the next
   * time it saves. `set_branding` writes `dark_color` and nulls `deep_color` in
   * the same patch, so no row can ever hold two different answers.
   */
  deep_color?: string;
  support_email?: string;
  main_site_url?: string;
}

/**
 * 🔴 THE BRANDING CONTRACT, AS DATA — so a narrower cannot silently drop a key.
 *
 * `apps/bidder` narrows the free-form `metadata.branding` jsonb before it
 * reaches the colour maths, and that narrower was hand-written with three keys
 * in it. When `deep_color` was added on 2026-09-11 the narrower was not updated,
 * so the app STRIPPED a house's chosen dark colour and silently fell back to the
 * derived one — the stored value reached the website and never reached the
 * phone. Nothing failed; the colour was just quietly wrong.
 *
 * Iterating this list instead means the next key added to `TenantBranding` is
 * carried by every narrower that reads it, with no second place to remember.
 */
export const BRANDING_CONTRACT_KEYS = [
  'primary_color',
  'accent_color',
  'secondary_color',
  'dark_color',
  // Both names stay in the contract: the narrowers must carry the legacy key
  // too, or a house that has not re-saved since the rename loses its colour on
  // the phone while keeping it on the website — the exact split this list was
  // created to prevent when `deep_color` itself was added.
  'deep_color',
  'logo_url',
  'logo',
] as const satisfies readonly (keyof TenantBranding)[];

/** The tenant-overridable brand tokens. Keys match the CSS custom properties the
 * app themes off. Partial: absent when the tenant set no primary color. */
export type BrandTokens = Partial<{
  '--brand-primary': string;
  '--brand-primary-hover': string;
  '--brand-primary-fg': string;
  '--brand-accent': string;
  '--brand-accent-fg': string;
  '--brand-link': string;
  /** The house's colour at near-black — the dark panel surface. */
  '--brand-deep': string;
  '--text-on-deep': string;
  /** A quieter step on the dark panel, floored at AA against it. */
  '--text-muted-on-deep': string;
  /**
   * The primary CTA, derived from the accent by `ctaFromAccent` — the same
   * function the native app calls. Emitted here rather than written by hand in
   * a stylesheet, because a hand-written copy is what let the app and the
   * website draw different primary buttons for the same tenant.
   */
  '--cta-bg': string;
  '--cta-fg': string;
  '--cta-edge': string;
}>;

function safeHex(v: string | null | undefined): RGB | null {
  if (v === null || v === undefined || v.trim() === '') return null;
  try {
    return hexToRgb(v);
  } catch {
    return null;
  }
}

/** Neutral background a surface renders on, per mode — used only to keep the
 * link color AA-legible. Matches the scheme neutrals in schemes.ts. */
const SURFACE_BG: Record<'light' | 'dark', RGB> = {
  light: { r: 255, g: 255, b: 255 },
  dark: { r: 23, g: 23, b: 27 },
};

function shiftLightness(rgb: RGB, delta: number): RGB {
  const hsl = rgbToHsl(rgb);
  return hslToRgb({ ...hsl, l: clamp(hsl.l + delta, 0, 1) });
}

/**
 * Resolve a tenant's branding to the AA-guaranteed --brand-* overrides for one
 * mode. Returns {} when no primary color is set → the surface keeps AuctiSync's
 * default brand tokens. The logo is handled separately (it's an <img>, not a token).
 */
export function brandingToTokens(
  branding: TenantBranding | null | undefined,
  mode: 'light' | 'dark' = 'light',
): BrandTokens {
  const primary = safeHex(branding?.primary_color);
  if (primary === null) return {};
  const accent = safeHex(branding?.accent_color ?? branding?.secondary_color) ?? primary;

  const p = accessibleFill(primary, mode);
  const a = accessibleFill(accent, mode);
  const hover = shiftLightness(p.fill, mode === 'light' ? -0.06 : 0.06);
  const link = ensureContrast(primary, SURFACE_BG[mode], AA_NORMAL);

  /**
   * 🔴 THE CTA IS EMITTED HERE, from the SAME function the native app uses.
   *
   * It used to be hand-written in `bidder-elevated.css` as
   * `--cta-bg: var(--brand-primary)` with `--cta-edge: transparent`, which is
   * the rule the app REPLACED in August — so a tenant's phone and their
   * website drew different primary buttons from the same brand. This is the
   * divergence collapsing: `ctaFromAccent` is the one implementation, and a
   * parity test asserts the two surfaces agree for the same brand.
   *
   * Derived from the ACCENT, measured against the ELEVATED surface the button
   * actually sits on, with an edge that is a real colour rather than
   * `transparent` — a pale accent on white is 1.15 against its ground and has
   * no shape without one.
   */
  const cta = ctaFromAccent(rgbToHex(a.fill), CTA_SURFACE_ELEV[mode], mode);

  /**
   * 🔴 STORED WINS OVER DERIVED. A house that has chosen its own dark colour
   * gets exactly that; everyone else gets one derived from their primary, so no
   * tenant is left with an unpainted panel.
   *
   * The stored value is used AS GIVEN — not pushed toward black, not
   * contrast-corrected. It is their brand, and silently "fixing" a colour a
   * house deliberately picked is worse than showing it. What IS guaranteed is
   * the FOREGROUND: `--text-on-deep` is chosen against whatever this turns out
   * to be, so a house that picks something pale gets dark text rather than an
   * unreadable panel.
   */
  // `dark_color` first, `deep_color` as the legacy fallback — the same
  // precedence as `accent_color ?? secondary_color` above.
  const storedDeep = safeHex(branding?.dark_color ?? branding?.deep_color);
  const deep = storedDeep ?? deepSurface(p.fill);
  const deepFg = bestTextOn(deep);

  return {
    '--brand-primary': rgbToHex(p.fill),
    '--brand-primary-hover': rgbToHex(hover),
    '--brand-primary-fg': rgbToHex(p.fg),
    '--brand-accent': rgbToHex(a.fill),
    '--brand-accent-fg': rgbToHex(a.fg),
    '--brand-link': rgbToHex(link),
    // 🔴 THE SAME `deepSurface` THE NATIVE THEME CALLS. The website's dark
    // panels (sale header, filter rail) and `bidderTheme.brandDeep` must be the
    // same colour for the same house — two derivations would be two things to
    // disagree about what a tenant's dark panel looks like.
    '--brand-deep': rgbToHex(deep),
    '--text-on-deep': rgbToHex(deepFg),
    // COMPUTED, not a CSS `color-mix` with a fixed percentage. A house can store
    // any colour here, so the muted step has to be floored against whatever it
    // turns out to be — see `mutedOn`.
    '--text-muted-on-deep': rgbToHex(mutedOn(deepFg, deep)),
    '--cta-bg': cta.bg,
    '--cta-fg': cta.fg,
    '--cta-edge': cta.edge,
  };
}

/** The tenant's logo URL, tolerating the legacy `logo` alias. Null when unset. */
export function brandingLogoUrl(branding: TenantBranding | null | undefined): string | null {
  const url = branding?.logo_url ?? branding?.logo;
  return typeof url === 'string' && url.trim() !== '' ? url : null;
}
